import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOnlineStore } from '@/store/onlineStore';
import { InvestmentDisclaimer } from '@/components/atoms/InvestmentDisclaimer';
import { PWAInstallBanner } from '@/components/atoms/PWAInstallBanner';
import { get } from '@/api/client';

interface SiteSetting {
  key: string;
  value: string | null;
  description: string | null;
  sort_order: number;
}

/**
 * 하단 푸터 — 실시간 접속자 수(회원/비회원), 약관 네비, 사업자 정보, 투자 면책조항.
 * 사업자 정보는 sisegonggan.site_settings (어드민 → 사이트 설정) 에서 편집.
 */
export function Footer() {
  const { members, guests } = useOnlineStore();
  const { data } = useQuery({
    queryKey: ['site-settings', 'public'],
    queryFn: () => get<{ settings: SiteSetting[] }>('/site-settings'),
    staleTime: 10 * 60_000,
  });

  // biz_ prefix 항목만 사업자 정보로 분류
  const biz = (data?.settings ?? []).filter((s) => s.key.startsWith('biz_'));

  /* 약관·정책 링크 — 모바일 카드 그리드와 데스크탑 인라인 양쪽에서 재사용 */
  const NAV_LINKS = [
    { to: '/board/notice-ops', label: '운영/공지' },
    { to: '/board/suggest',    label: '건의/제안' },
    { to: '/terms',            label: '이용약관' },
    { to: '/privacy',          label: '개인정보처리방침' },
    { to: '/disclaimer',       label: '투자 위험 고지' },
  ];

  return (
    <footer className="border-t border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="container-base py-5">
        {/* 1행: 브랜드 + 실시간 접속 (모바일도 한 줄 유지 — wrap 으로 좁을 때 자동 줄바꿈) */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-extrabold text-[var(--color-text)]">시세공간</span>
            <span className="text-[11px] text-[var(--color-text-sub)]">© 2026</span>
          </div>

          {/* 라이브 닷 + 접속자 수 — pill (높이 축소) */}
          <div
            aria-live="polite"
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1 text-[11px] text-[var(--color-text-sub)]"
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/70" />
              <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            <span>
              회원 <span className="font-semibold text-[var(--color-text)]">{members.toLocaleString()}</span>
              <span aria-hidden className="mx-1 text-[var(--color-border)]">·</span>
              비회원 <span className="font-semibold text-[var(--color-text)]">{guests.toLocaleString()}</span>
            </span>
          </div>
        </div>

        {/* 2행: 약관 네비 — 모바일/PC 동일하게 인라인 wrap (카드 그리드 제거로 height 절감).
            글로벌 폰트 최소 정책에 맞춰 text-xs(14px) 유지 — 가독성 우선 영역 */}
        <nav className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--color-text-sub)]">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-[var(--color-text)]">
              {l.label}
            </Link>
          ))}
        </nav>

        {/* 3행: 사업자 정보 — 모바일/PC 모두 인라인 wrap (dl 카드 펼침 제거).
            푸터 한정 가독성 예외 적용 — 12px (법적 표기 컴팩트화) */}
        {biz.length > 0 && (
          <address className="mt-2 not-italic">
            <p className="flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] leading-snug text-[var(--color-text-sub)]">
              {biz.map((s) => (
                <span key={s.key} className="inline-flex items-center gap-2">
                  <span>{s.description ?? s.key}</span>
                  <span className="text-[var(--color-text)]">{s.value}</span>
                </span>
              ))}
            </p>
          </address>
        )}

      </div>
      <InvestmentDisclaimer variant="compact" />
      {/* 모바일 + 미설치 시 즉시 노출되는 PWA 설치 안내. PC/설치됨/iOS Safari 에서는 자동 숨김. */}
      <PWAInstallBanner />
    </footer>
  );
}
